import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import "highlight.js/styles/github.css";
import { chatbotClasses } from "../styles/chatbotStyles";

// Copy button component
function CopyButton({ content }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center justify-center w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
      title="Copy code"
    >
      {copied ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-green-600"
        >
          <polyline points="20,6 9,17 4,12"></polyline>
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-gray-600"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      )}
    </button>
  );
}

export default function MarkdownRenderer({ text, className = "" }) {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Inline code
          code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
              return (
                <code
                  className={`inline-code bg-gray-100 rounded font-mono ${chatbotClasses.inlineCode}`}
                  {...props}
                >
                  {children}
                </code>
              );
            }

            return (
              <code className="max-w-full break-words" {...props}>
                {children}
              </code>
            );
          },

          // Code blocks with language label and copy button
          pre: ({ children, ...props }) => {
            const codeElement = children?.props;
            const codeContent = String(codeElement?.children || "").replace(
              /\n$/,
              "",
            );
            const className = codeElement?.className || "";
            const match = /language-(\w+)/.exec(className);
            const language = match ? match[1] : "";

            return (
              <div
                className={`code-block bg-gray-50 border border-gray-200 rounded-lg max-w-full ${chatbotClasses.codeBlock.container}`}
              >
                {/* Header with language and copy button */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 border-b border-gray-200 rounded-t-lg">
                  <span
                    className={`code-language font-medium text-gray-600 ${chatbotClasses.codeBlock.language}`}
                  >
                    {language || "text"}
                  </span>
                  <CopyButton content={codeContent} />
                </div>
                {/* Code content */}
                <pre
                  className={`p-3 overflow-x-auto whitespace-pre max-w-full ${chatbotClasses.codeBlock.code}`}
                  {...props}
                >
                  {children}
                </pre>
              </div>
            );
          },

          // Links with proper styling
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              className="text-blue-600 hover:text-blue-800 underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),

          // Tables (from remark-gfm)
          table: ({ children, ...props }) => (
            <div className={`overflow-x-auto ${chatbotClasses.message.table}`}>
              <table
                className="min-w-full border border-gray-200 rounded-lg"
                {...props}
              >
                {children}
              </table>
            </div>
          ),

          th: ({ children, ...props }) => (
            <th
              className={`bg-gray-100 border-b border-gray-200 text-left font-medium text-gray-700 ${chatbotClasses.message.tableCell}`}
              {...props}
            >
              {children}
            </th>
          ),

          td: ({ children, ...props }) => (
            <td
              className={`border-b border-gray-200 ${chatbotClasses.message.tableCell}`}
              {...props}
            >
              {children}
            </td>
          ),

          // Blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              className={`border-l-4 border-gray-300 pl-4 italic text-gray-600 ${chatbotClasses.message.blockquote}`}
              {...props}
            >
              {children}
            </blockquote>
          ),

          // Lists
          ul: ({ children, ...props }) => (
            <ul
              className={`list-disc list-inside space-y-1 ${chatbotClasses.message.list}`}
              {...props}
            >
              {children}
            </ul>
          ),

          ol: ({ children, ...props }) => (
            <ol
              className={`list-decimal list-inside space-y-1 ${chatbotClasses.message.list}`}
              {...props}
            >
              {children}
            </ol>
          ),

          // Headings
          h1: ({ children, ...props }) => (
            <h1
              className={`font-bold ${chatbotClasses.message.heading1}`}
              {...props}
            >
              {children}
            </h1>
          ),

          h2: ({ children, ...props }) => (
            <h2
              className={`font-bold ${chatbotClasses.message.heading2}`}
              {...props}
            >
              {children}
            </h2>
          ),

          h3: ({ children, ...props }) => (
            <h3
              className={`font-bold ${chatbotClasses.message.heading3}`}
              {...props}
            >
              {children}
            </h3>
          ),

          h4: ({ children, ...props }) => (
            <h4
              className={`font-bold ${chatbotClasses.message.heading4}`}
              {...props}
            >
              {children}
            </h4>
          ),

          h5: ({ children, ...props }) => (
            <h5
              className={`font-bold ${chatbotClasses.message.heading5}`}
              {...props}
            >
              {children}
            </h5>
          ),

          h6: ({ children, ...props }) => (
            <h6
              className={`font-bold ${chatbotClasses.message.heading6}`}
              {...props}
            >
              {children}
            </h6>
          ),

          // Paragraphs
          p: ({ children, ...props }) => (
            <p
              className={`leading-relaxed ${chatbotClasses.message.paragraph}`}
              {...props}
            >
              {children}
            </p>
          ),

          // Horizontal rule
          hr: ({ ...props }) => (
            <hr className="my-4 border-gray-300" {...props} />
          ),

          // Strikethrough (from remark-gfm)
          del: ({ children, ...props }) => (
            <del className="line-through" {...props}>
              {children}
            </del>
          ),
        }}
      >
        {text || ""}
      </ReactMarkdown>
    </div>
  );
}
